import {
  INSURANCE_TYPE_CFMR,
  INSURANCE_TYPE_PASSPORT_CARD,
  PASSPORT_CARD_INSURANCE_LABEL,
  PROVIDER_PASSPORT_CARD,
} from '~/consts/insurance';

import { dateNow, diffDays } from '~/services/TimeService';

import currencyFormatter, { addDollarType } from './currencyFormatter';

/**
 * Detects Passport Card insurance items. Mirrors the backend's
 * `isPassportCardInsuranceItem` (svc-order), which matches on either the
 * provider or the insurance type, since older items may only carry one.
 */
export const isPassportCardInsurance = (item: { provider?: string | null; insurance_type?: string | null }): boolean =>
  item.provider === PROVIDER_PASSPORT_CARD || item.insurance_type === INSURANCE_TYPE_PASSPORT_CARD;

/**
 * Detects "Cancel For Many Reasons" (CFMR) items. CFMR is a cancellation-waiver
 * product grouped under insurance, so its copy is relabelled ("Travel
 * Protection") to avoid presenting it to agents as an insurance product.
 */
export const isCfmr = (item: { insurance_type?: string | null }): boolean =>
  item.insurance_type === INSURANCE_TYPE_CFMR;

/**
 * Label for the "Update" action on an insurance item — reads as "Travel
 * Protection" for CFMR, otherwise the standard insurance wording.
 */
export const getUpdateInsuranceLabel = (item: { insurance_type?: string | null }): string =>
  isCfmr(item) ? 'Update Travel Protection' : 'Update insurance';

/**
 * Product label for an insurance item in list views. PassportCard sells a single product
 * whose raw code is unreadable, so it gets a fixed short label; every other provider keeps
 * the product code admin has always shown.
 */
export const getInsuranceProductLabel = (item: {
  product_id?: string | null;
  provider?: string | null;
  insurance_type?: string | null;
}): string => (isPassportCardInsurance(item) ? PASSPORT_CARD_INSURANCE_LABEL : (item.product_id ?? ''));

/**
 * Whole-year age from a date of birth. Returns `null` for a missing/invalid DOB
 * so callers can render a blank field rather than "NaN".
 */
export const getAgeFromDateOfBirth = (dateOfBirth?: string | null): number | null => {
  if (!dateOfBirth) {
    return null;
  }
  const age = diffDays(dateNow(), dateOfBirth, 'year');
  return Number.isFinite(age) && age >= 0 ? Math.floor(age) : null;
};

export const getUpdatedPriceDiff = (
  isLPMember: boolean,
  platform: string,
  pricesDiff: { totalPriceDiff: number; luxPlusPriceDiff: number; mobilePriceDiff: number },
) => {
  if (isLPMember) {
    return pricesDiff.luxPlusPriceDiff;
  }

  if (platform === 'mobile') {
    return pricesDiff.mobilePriceDiff;
  }

  return pricesDiff.totalPriceDiff;
};

interface InsuranceUpdatePriceChangeOptions {
  /**
   * Whether svc-order will refund a decrease to the customer's original payment method on its
   * own. It does not for business or agent hub orders (it posts a Slack alert in #svc-insurance
   * for CS instead),
   * so the copy must not promise a refund on those.
   */
  refundsAutomatically?: boolean;
}

/**
 * Copy for the admin "Update insurance" dialog once the provider has re-quoted the policy.
 * The dialog only confirms updates that need no payment (a dearer policy is sent to the
 * payment page), so a negative difference is the interesting case: svc-order refunds it to
 * the customer's original payment method when the update is confirmed, and the agent should
 * say so.
 */
export const getInsuranceUpdatePriceChangeLabel = (
  totalPriceDiff: number,
  currencyCode: string,
  { refundsAutomatically = true }: InsuranceUpdatePriceChangeOptions = {},
): string => {
  // Formatted in en-AU regardless of the agent's machine locale (`formatCurrency`'s region
  // lookup falls through to the machine default), from the absolute value so the currency
  // letter is kept and the sign added here: "-A$30.00", not "-$30.00".
  const amount = addDollarType(
    currencyFormatter(currencyCode, Math.abs(totalPriceDiff), 2, 'narrowSymbol'),
    currencyCode,
  );

  if (totalPriceDiff < 0) {
    if (!refundsAutomatically) {
      return `Price difference: -${amount}. This order is not refunded automatically (business or agent hub order); a Slack alert in #svc-insurance asks CS to handle the difference manually.`;
    }
    return `Price difference: -${amount}. The customer will be refunded this amount to their original payment method once confirmed.`;
  }

  if (totalPriceDiff === 0) {
    return 'No additional payment required.';
  }

  // StepConfirm sends a dearer policy to the payment page, so StepResult never reaches this
  // branch. Kept so the helper stays total: a positive difference must never read as a refund.
  return `Total amount to pay: ${amount}`;
};
